import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { segmentsAPI, clientsAPI, Segment, Client } from '../../services/api';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  FunnelIcon,
  CalendarIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  PlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Badge, Spinner, EmptyState, Modal, SearchInput } from '../ui';

const SegmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const basePath = isAdminRoute ? '/admin/segments' : '/segments';
  const clientBasePath = isAdminRoute ? '/admin/clients' : '/clients';

  const [segment, setSegment] = useState<Segment | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Client selector modal state
  const [showAddClientsModal, setShowAddClientsModal] = useState(false);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [addingClients, setAddingClients] = useState(false);
  const [loadingAvailableClients, setLoadingAvailableClients] = useState(false);

  useEffect(() => {
    fetchSegment();
    fetchClients();
  }, [id, pagination.page]);

  const fetchSegment = async () => {
    try {
      setLoading(true);
      const data = await segmentsAPI.getById(parseInt(id!));
      setSegment(data);
    } catch (error) {
      console.error('Error fetching segment:', error);
      alert('Failed to load segment');
      navigate(basePath);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      const response = await segmentsAPI.getClients(
        parseInt(id!),
        pagination.page,
        pagination.limit
      );
      setClients(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this segment?')) {
      return;
    }

    try {
      await segmentsAPI.delete(parseInt(id!));
      navigate(basePath);
    } catch (error) {
      console.error('Error deleting segment:', error);
      alert('Failed to delete segment');
    }
  };

  const handleRemoveClient = async (clientId: number) => {
    if (!window.confirm('Remove this client from the segment?')) {
      return;
    }

    try {
      await segmentsAPI.removeClients(parseInt(id!), [clientId]);
      fetchClients();
      fetchSegment(); // Refresh to update client count
    } catch (error) {
      console.error('Error removing client:', error);
      alert('Failed to remove client');
    }
  };

  // Fetch available clients for adding to segment
  const fetchAvailableClients = async (search: string = '') => {
    try {
      setLoadingAvailableClients(true);
      const response = await clientsAPI.getAll({ search, limit: 50 });
      // Filter out clients already in the segment
      const existingClientIds = clients.map(c => c.id);
      const available = (response.data || response).filter(
        (c: Client) => !existingClientIds.includes(c.id)
      );
      setAvailableClients(available);
    } catch (error) {
      console.error('Error fetching available clients:', error);
    } finally {
      setLoadingAvailableClients(false);
    }
  };

  const handleOpenAddClientsModal = () => {
    setSelectedClientIds([]);
    setClientSearch('');
    setShowAddClientsModal(true);
    fetchAvailableClients();
  };

  const handleClientSearchChange = (value: string) => {
    setClientSearch(value);
    fetchAvailableClients(value);
  };

  const toggleClientSelection = (clientId: number) => {
    setSelectedClientIds(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleAddClients = async () => {
    if (selectedClientIds.length === 0) return;

    try {
      setAddingClients(true);
      await segmentsAPI.addClients(parseInt(id!), selectedClientIds);
      setShowAddClientsModal(false);
      fetchClients();
      fetchSegment(); // Refresh to update client count
    } catch (error) {
      console.error('Error adding clients:', error);
      alert('Failed to add clients');
    } finally {
      setAddingClients(false);
    }
  };

  const getImportanceVariant = (importance: string): 'error' | 'warning' | 'primary' | 'neutral' | 'success' | 'secondary' => {
    const variants: Record<string, 'error' | 'warning' | 'primary' | 'neutral' | 'success' | 'secondary'> = {
      VERY_HIGH: 'error',
      HIGH: 'warning',
      MEDIUM: 'primary',
      LOW: 'secondary',
      VERY_LOW: 'neutral',
    };
    return variants[importance] || 'neutral';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!segment) {
    return null;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(basePath)}
          className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Segments
        </button>

        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center shadow-lg">
                <FunnelIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-neutral-900">{segment.name}</h1>
                <p className="mt-1 text-sm text-neutral-600">{segment.description || 'No description'}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link to={`${basePath}/${segment.id}/edit`}>
              <Button variant="outline" leftIcon={<PencilIcon className="h-5 w-5" />}>
                Edit
              </Button>
            </Link>
            <Button variant="danger" onClick={handleDelete} leftIcon={<TrashIcon className="h-5 w-5" />}>
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-100 text-sm font-medium">Total Clients</p>
              <p className="text-3xl font-bold mt-2">{segment._count?.clients || 0}</p>
            </div>
            <UsersIcon className="h-12 w-12 text-primary-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-2xl shadow-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-secondary-100 text-sm font-medium">Segment Type</p>
              <p className="text-2xl font-bold mt-2 capitalize">{segment.type.toLowerCase()}</p>
            </div>
            <FunnelIcon className="h-12 w-12 text-secondary-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-success-500 to-success-600 rounded-2xl shadow-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-success-100 text-sm font-medium">Created</p>
              <p className="text-lg font-bold mt-2">{new Date(segment.createdAt).toLocaleDateString()}</p>
            </div>
            <CalendarIcon className="h-12 w-12 text-success-200" />
          </div>
        </div>
      </div>

      {/* Segment Info */}
      <Card variant="elevated" className="mb-8">
        <CardBody className="p-6">
          <h2 className="text-xl font-bold text-neutral-900 mb-4">Segment Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Created By</label>
              <p className="text-neutral-900">
                {segment.user ? `${segment.user.firstName} ${segment.user.lastName}` : 'Unknown'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Last Updated</label>
              <p className="text-neutral-900">{new Date(segment.updatedAt).toLocaleString()}</p>
            </div>
            {segment.type === 'DYNAMIC' && segment.criteria && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-neutral-700 mb-2">Criteria</label>
                <pre className="bg-neutral-50 p-4 rounded-lg text-sm overflow-x-auto border border-neutral-200">
                  {JSON.stringify(segment.criteria, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Clients List */}
      <Card variant="elevated" className="overflow-hidden">
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Clients in Segment</h2>
          {segment.type === 'STATIC' && (
            <Button
              variant="secondary"
              leftIcon={<PlusIcon className="h-5 w-5" />}
              onClick={handleOpenAddClientsModal}
            >
              Add Clients
            </Button>
          )}
        </div>

        {clientsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" />
          </div>
        ) : clients.length === 0 ? (
          <EmptyState
            icon={<UsersIcon className="h-12 w-12" />}
            title="No clients"
            description="This segment doesn't have any clients yet."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Importance
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {clients.map((client) => (
                    <tr key={client.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                            <UserIcon className="h-6 w-6 text-white" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-neutral-900">{client.name}</div>
                            <div className="text-sm text-neutral-500 capitalize">{client.type.toLowerCase()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-neutral-900">
                          {client.email && (
                            <div className="flex items-center mb-1">
                              <EnvelopeIcon className="h-4 w-4 mr-2 text-neutral-400" />
                              {client.email}
                            </div>
                          )}
                          {client.phoneNumbers && client.phoneNumbers.length > 0 && (
                            <div className="flex items-center">
                              <PhoneIcon className="h-4 w-4 mr-2 text-neutral-400" />
                              {client.phoneNumbers[0]}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="primary">{client.type}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getImportanceVariant(client.importance)}>
                          {client.importance.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`${clientBasePath}/${client.id}`}
                          className="text-primary-600 hover:text-primary-900 mr-4"
                        >
                          View
                        </Link>
                        {segment.type === 'STATIC' && (
                          <button
                            onClick={() => handleRemoveClient(client.id)}
                            className="text-error-600 hover:text-error-900"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-neutral-50 px-6 py-4 flex items-center justify-between border-t border-neutral-200">
                <div className="text-sm text-neutral-700">
                  Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> clients
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Add Clients Modal */}
      <Modal
        isOpen={showAddClientsModal}
        onClose={() => setShowAddClientsModal(false)}
        title="Add Clients to Segment"
        subtitle={`Select clients to add to "${segment.name}"`}
        size="lg"
      >
        <div className="space-y-4">
          {/* Search Input */}
          <SearchInput
            value={clientSearch}
            onChange={handleClientSearchChange}
            placeholder="Search clients by name, email..."
            onClear={() => handleClientSearchChange('')}
          />

          {/* Client List */}
          <div className="max-h-96 overflow-y-auto border border-neutral-200 rounded-lg">
            {loadingAvailableClients ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="md" />
              </div>
            ) : availableClients.length === 0 ? (
              <div className="text-center py-12 text-neutral-500">
                <UsersIcon className="h-10 w-10 mx-auto mb-2 text-neutral-400" />
                <p className="text-sm">
                  {clientSearch ? 'No clients found matching your search' : 'No available clients to add'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-200">
                {availableClients.map((client) => (
                  <label
                    key={client.id}
                    className={`flex items-center gap-4 p-4 cursor-pointer transition-colors ${
                      selectedClientIds.includes(client.id)
                        ? 'bg-primary-50'
                        : 'hover:bg-neutral-50'
                    }`}
                  >
                    <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedClientIds.includes(client.id)
                        ? 'bg-primary-600 border-primary-600'
                        : 'border-neutral-300'
                    }`}>
                      {selectedClientIds.includes(client.id) && (
                        <CheckIcon className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedClientIds.includes(client.id)}
                      onChange={() => toggleClientSelection(client.id)}
                      className="sr-only"
                    />
                    <div className="h-10 w-10 flex-shrink-0 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-900 truncate">
                        {client.name}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-neutral-500">
                        <span className="capitalize">{client.type.toLowerCase()}</span>
                        {client.email && (
                          <span className="flex items-center">
                            <EnvelopeIcon className="h-3 w-3 mr-1" />
                            {client.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge variant={
                      client.importance === 'VERY_HIGH' || client.importance === 'HIGH' ? 'error' :
                      client.importance === 'MEDIUM' ? 'warning' : 'neutral'
                    } size="sm">
                      {client.importance.replace('_', ' ')}
                    </Badge>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Selected Count & Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-neutral-200">
            <div className="text-sm text-neutral-600">
              {selectedClientIds.length > 0 ? (
                <span className="font-medium text-primary-600">
                  {selectedClientIds.length} client{selectedClientIds.length !== 1 ? 's' : ''} selected
                </span>
              ) : (
                'Select clients to add'
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddClientsModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleAddClients}
                disabled={selectedClientIds.length === 0 || addingClients}
                leftIcon={addingClients ? <Spinner size="sm" /> : <PlusIcon className="h-4 w-4" />}
              >
                {addingClients ? 'Adding...' : `Add ${selectedClientIds.length || ''} Client${selectedClientIds.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SegmentDetail;

